import { API_ORIGIN } from "../config/api";

// src/services/interviewBackend.js
const API_BASE = import.meta?.env?.VITE_API_URL || API_ORIGIN;

function getToken() {
  return (
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    ""
  );
}

async function apiFetch(path, { method = "GET", body, headers } = {}) {
  const token = getToken();

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(headers || {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg =
      data?.error ||
      data?.message ||
      `Request failed (${res.status}) on ${path}`;
    throw new Error(msg);
  }

  return data;
}

async function apiFetchAny(paths, options = {}) {
  let lastError = null;
  for (const path of paths) {
    try {
      return await apiFetch(path, options);
    } catch (err) {
      lastError = err;
      const msg = String(err?.message || '');
      if (!msg.includes('(404)') && !msg.toLowerCase().includes('not found')) {
        throw err;
      }
    }
  }
  throw lastError || new Error('All API endpoints failed');
}

export function mapInterviewTypeToBackend(type) {
  if (type === "CODING") return "technical_coding";
  if (type === "SYSTEM_DESIGN") return "technical_system_design";
  if (type === "DEEP_DIVE") return "technical_deep_dive";
  return "technical";
}

export function buildIntro(type, mode, topic) {
  const parts = [];
  parts.push(
    `Welcome. We'll run a ${String(type).replaceAll("_", " ").toLowerCase()} session.`
  );
  if (mode) parts.push(`Mode: ${String(mode).replaceAll("_", " ").toLowerCase()}.`);
  if (topic) parts.push(`Topic: ${topic}.`);
  parts.push(
    "Rules: explain briefly, then execute. I will challenge assumptions and edge-cases."
  );
  return parts.join(" ");
}

export function clarityLabelFromScore(n) {
  const v = Number.isFinite(n) ? n : 0;
  if (v >= 80) return "High";
  if (v >= 60) return "Medium";
  return "Low";
}

export function seniorityFromScore(score) {
  const s = Number.isFinite(score) ? score : 0;
  if (s >= 90) return "Staff";
  if (s >= 80) return "Senior";
  if (s >= 65) return "Mid-Level";
  return "Junior";
}

export function clampScore(n) {
  const v = Number.isFinite(n) ? n : 0;
  return Math.max(0, Math.min(100, Math.round(v)));
}

/* ----------------------------- Normalizers ----------------------------- */

function pickFirstString(obj, keys) {
  if (!obj || typeof obj !== "object") return "";
  for (const k of keys) {
    const v = obj?.[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

function stringifyInsightItem(item) {
  if (!item) return "";
  if (typeof item === "string") return item.trim();

  if (typeof item === "object") {
    // get_performance_insights() items are often objects with description/action/detail, etc.
    const s =
      pickFirstString(item, [
        "action",
        "description",
        "detail",
        "example",
        "title",
        "message",
        "feedback",
      ]) ||
      (typeof item.category === "string" &&
      item.category &&
      typeof item.impact === "string"
        ? `${item.category}: ${item.impact}`
        : "");

    if (s) return s;

    // Last resort: stable JSON (prevents UI from becoming empty)
    try {
      return JSON.stringify(item);
    } catch {
      return "";
    }
  }

  return "";
}

function normalizeList(list) {
  const arr = Array.isArray(list) ? list : [];
  return arr
    .map(stringifyInsightItem)
    .map((s) => (s || "").trim())
    .filter(Boolean);
}

/* ----------------------------- API Calls ----------------------------- */

export async function checkIfCanUseInterview() {
  return apiFetch("/api/subscriptions/use-interview", {
    method: "POST"
  });
}

export async function createInterviewSession({
  type,
  duration = 30,
  selectedFormat = "chat",
} = {}) {
  return apiFetch("/api/interview/create", {
    method: "POST",
    body: {
      selectedFormat,
      duration,
      interviewType: mapInterviewTypeToBackend(type),
      enableFollowUps: true,
      surpriseQuestions: false,
      adaptiveDifficulty: false,
      personality: {
        selectedPersona: { id: "neutral", name: "Neutral" },
        accent: "american",
        tone: 3,
        difficulty: 3,
        pace: 3,
        pressure: 3,
      },
    },
  });
}

export async function analyzeTurn({
  text,
  question = "",
  durationSec = 10,
  delaySec = 2,
  useAI = true,
  toxicity = null,
} = {}) {
  return apiFetch("/api/interview/analyze", {
    method: "POST",
    body: {
      text,
      question,
      duration: durationSec,
      delay: delaySec,
      toxicity,
      use_ai: useAI,
    },
  });
}

export async function finalAnalysis({
  answers,
  fillerWords = {},
  toxicity = null,
  useAI = true,
} = {}) {
  return apiFetch("/api/interview/final-analysis", {
    method: "POST",
    body: {
      answers,
      fillerWords,
      toxicity,
      use_ai: useAI,
    },
  });
}

/* ----------------------------- UI Mappers ----------------------------- */

export function turnAnalyzeToUI(analyzeRes) {
  // Flask analyze response: final_score, local_analysis.overall_score, local_analysis.metrics, ai_analysis...
  const finalScore = clampScore(
    analyzeRes?.final_score ??
      analyzeRes?.ai_analysis?.ai_score ??
      analyzeRes?.local_analysis?.overall_score ??
      0
  );

  const clarityScore =
    analyzeRes?.local_analysis?.metrics?.clarity ??
    analyzeRes?.ai_analysis?.validated_metrics?.clarity ??
    0;

  const metrics = {
    correctness: finalScore,
    clarity: clarityLabelFromScore(Number(clarityScore) || 0),
  };

  // Prefer AI detailed feedback; fallback to local overall_assessment; fallback to top recommendation
  const aiDetailed =
    typeof analyzeRes?.ai_analysis?.detailed_feedback === "string"
      ? analyzeRes.ai_analysis.detailed_feedback.trim()
      : "";

  const localOverall =
    typeof analyzeRes?.local_analysis?.insights?.overall_assessment === "string"
      ? analyzeRes.local_analysis.insights.overall_assessment.trim()
      : "";

  const recText = normalizeList(analyzeRes?.local_analysis?.insights?.recommendations)[0] || "";

  const baseText =
    aiDetailed ||
    localOverall ||
    (recText ? `Recommendation: ${recText}` : "") ||
    "Noted. Continue.";

  const improvements = normalizeList(analyzeRes?.ai_analysis?.specific_improvements);
  const extra =
    improvements.length > 0
      ? `\n\nNext improvements:\n- ${improvements.slice(0, 3).join("\n- ")}`
      : "";

  return {
    metrics,
    aiText: `${baseText}${extra}`.trim(),
    finalScore,
  };
}

export function finalAnalysisToFeedbackData(res) {
  // Flask final-analysis response:
  // final_score, local_analysis.final_score, local_analysis.statistics.average_metrics, local_analysis.insights,
  // ai_analysis.overall_assessment, ai_analysis.detailed_strengths, ai_analysis.detailed_weaknesses
  const score = clampScore(res?.final_score ?? res?.local_analysis?.final_score ?? 0);
  const seniority = seniorityFromScore(score);

  const strengths = normalizeList(
    res?.ai_analysis?.detailed_strengths ?? res?.local_analysis?.insights?.strengths ?? []
  );

  const weaknesses = normalizeList(
    res?.ai_analysis?.detailed_weaknesses ??
      res?.local_analysis?.insights?.areas_for_improvement ??
      []
  );

  const avg =
    res?.local_analysis?.statistics?.average_metrics ||
    res?.local_analysis?.metrics ||
    {};

  const radarData = [
    { subject: "Clarity", A: clampScore(avg?.clarity), fullMark: 100 },
    { subject: "Pacing", A: clampScore(avg?.pacing), fullMark: 100 },
    { subject: "Professionalism", A: clampScore(avg?.professionalism), fullMark: 100 },
    { subject: "Tone", A: clampScore(avg?.tone), fullMark: 100 },
    { subject: "Response Time", A: clampScore(avg?.response_time), fullMark: 100 },
  ];

  const summary =
    (typeof res?.ai_analysis?.overall_assessment === "string" &&
      res.ai_analysis.overall_assessment.trim()) ||
    (typeof res?.local_analysis?.insights?.overall_assessment === "string" &&
      res.local_analysis.insights.overall_assessment.trim()) ||
    "Session completed. Review your strengths and focus areas.";

  return { score, seniority, strengths, weaknesses, radarData, summary };
}


export function normalizeGeneratedQuestions(rawQuestions = []) {
  const arr = Array.isArray(rawQuestions) ? rawQuestions : [];
  return arr
    .map((q, index) => {
      if (typeof q === "string") {
        return {
          id: `q_${index + 1}`,
          text: q.trim(),
          type: "technical",
          expectedSignals: [],
        };
      }
      if (q && typeof q === "object") {
        const text = q.question || q.text || q.prompt || q.title || q.content || "";
        return {
          id: q.id || `q_${index + 1}`,
          text: String(text || "").trim(),
          type: q.type || q.category || "technical",
          expectedSignals: Array.isArray(q.expectedSignals)
            ? q.expectedSignals
            : Array.isArray(q.expected_signals)
            ? q.expected_signals
            : [],
          raw: q,
        };
      }
      return null;
    })
    .filter((q) => q?.text);
}

export async function generateTechnicalQuestions({
  interviewId,
  type = "CODING",
  mode = "LIVE",
  topic = "Algorithms",
  questionCount = 5,
  difficulty = "medium",
  role = "Software Engineer",
  industry = "Technology",
  tone = 3,
  difficultyLevel = 3,
  voiceId = null,
} = {}) {
  const interviewType = mapInterviewTypeToBackend(type);
  const focus = [String(type || "technical").replaceAll("_", " "), mode, topic]
    .filter(Boolean)
    .join(" / ");

  const data = await apiFetch("/api/interview/generate-questions", {
    method: "POST",
    body: {
      interviewId,
      questionCount,
      interviewType,
      jobRole: role || "Software Engineer",
      industry: industry || "Technology",
      difficulty,
      personaStyle: "technical interviewer",
      tone,
      difficulty_level: difficultyLevel,
      enableFollowUps: true,
      starMethodEnforcement: false,
      voiceId,
      context: `Generate practical technical interview questions for ${focus}. Avoid generic behavioral questions.`,
      topic,
      mode,
    },
  });

  const normalizedQuestions = normalizeGeneratedQuestions(data?.questions);

  if (data?.used_fallback) {
    throw new Error(
      "AI question generation returned fallback/mock questions. Check Groq API key, model response parsing, and /api/interview/generate-questions logs."
    );
  }

  if (!normalizedQuestions.length) {
    throw new Error("No real AI questions were returned by the backend.");
  }

  return {
    ...data,
    normalizedQuestions,
  };
}

export async function submitInterviewResults({ interview_id, payload } = {}) {
  if (!interview_id) throw new Error("Missing interview_id");
  return apiFetch(`/api/interview/${interview_id}/results`, {
    method: "PUT",
    body: payload,
  });
}

/* ----------------------------- Code Editor (Run/Submit/Drafts) ----------------------------- */

export async function runCode({ session_id, language, code, tests = "" } = {}) {
  return apiFetchAny(["/api/interview/code/run", "/api/code/run"], {
    method: "POST",
    body: { session_id, language, code, tests },
  });
}

export async function submitCode({
  session_id,
  language,
  code,
  tests = "",
  notes = "",
  question = "",
} = {}) {
  return apiFetchAny(["/api/interview/code/submit", "/api/code/submit"], {
    method: "POST",
    body: { session_id, language, code, tests, notes, question },
  });
}

export async function checkpointCodeDraft({
  session_id,
  key = "draft",
  language,
  code,
  tests = "",
  notes = "",
} = {}) {
  return apiFetchAny(["/api/interview/code/checkpoint", "/api/code/checkpoint"], {
    method: "POST",
    body: { session_id, key, language, code, tests, notes },
  });
}

export async function loadCodeDraft({ session_id, key = "draft" } = {}) {
  const qs = new URLSearchParams({
    session_id: String(session_id || ""),
    key,
  }).toString();
  return apiFetchAny([`/api/interview/code/draft?${qs}`, `/api/code/checkpoint?${qs}`], { method: "GET" });
}

export async function formatPythonCode({ code } = {}) {
  return apiFetchAny(["/api/interview/code/format", "/api/code/format"], {
    method: "POST",
    body: { language: "python", code },
  });
}



export async function saveInterviewResults({
  interview_id,
  score = 0,
  summary = "",
  strengths = [],
  weaknesses = [],
  radarData = [],
  answers = [],
  raw = {},
} = {}) {
  if (!interview_id) return null;

  const performance_categories = {
    communication: {
      score:
        radarData.find((x) => x.subject === "Clarity")?.A ??
        radarData.find((x) => x.name === "Clarity")?.value ??
        score,
    },
    problem_solving: {
      score:
        radarData.find((x) => x.subject === "Problem Solving")?.A ??
        radarData.find((x) => x.name === "Problem Solving")?.value ??
        score,
    },
    technical_skills: {
      score:
        radarData.find((x) => x.subject === "Correctness")?.A ??
        radarData.find((x) => x.name === "Correctness")?.value ??
        score,
    },
  };

  return apiFetch(`/api/interview/${interview_id}/results`, {
    method: "PUT",
    body: {
      overall_score: score,
      performance_score: score,
      feedback: summary,
      performance_categories,
      strengths,
      weakest_areas: weaknesses,
      detailed_feedback: answers.map((a, index) => ({
        question_number: index + 1,
        question: a.question,
        answer: a.answer || a.text,
        score: a.score,
        feedback: a.feedback,
      })),
      answer_reviews: answers,
      improvement_suggestions: weaknesses,
      next_steps: {
        title: "Recommended next practice",
        items: weaknesses,
      },
      total_questions_asked: answers.length,
      raw_ai_final_analysis: raw,
    },
  });
}

export function buildFinalAnswerPayload(answer = {}) {
  const question =
    answer.question ||
    answer.questionText ||
    answer.prompt ||
    answer.currentQuestion?.question ||
    answer.currentQuestion?.text ||
    "";

  const answerText =
    answer.answer ||
    answer.text ||
    answer.response ||
    answer.code ||
    answer.notes ||
    "";

  const score = clampScore(
    answer.score ??
      answer.finalScore ??
      answer.final_score ??
      answer.metrics?.correctness ??
      answer.analysis?.finalScore ??
      answer.analysis?.final_score ??
      0
  );

  const feedback =
    answer.feedback ||
    answer.aiText ||
    answer.ai_text ||
    answer.analysis?.aiText ||
    answer.analysis?.ai_text ||
    "";

  return {
    question: String(question || "").trim(),
    answer: String(answerText || "").trim(),
    text: String(answerText || "").trim(),

    duration: Number(answer.duration ?? answer.durationSec ?? answer.duration_seconds ?? 30),
    delay: Number(answer.delay ?? answer.delaySec ?? answer.delay_seconds ?? 2),

    score,
    finalScore: score,
    feedback: String(feedback || "").trim(),

    metrics: answer.metrics || answer.analysis?.metrics || {},
    type: answer.type || answer.questionType || answer.currentQuestion?.type || "technical",

    language: answer.language || "",
    code: answer.code || "",
    tests: answer.tests || "",
    notes: answer.notes || "",

    run: answer.run || answer.runResult || null,
    submitted_at: answer.submitted_at || new Date().toISOString(),
  };
}